import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react'
import OpenAI from 'openai'
import { useSettings } from './SettingsContext'

const ChatContext = createContext()

export function ChatProvider({ children }) {
  const [messages, setMessages] = useState([])
  const [mode, setMode] = useState('normal')
  const [error, setError] = useState(null)
  const [detectedLanguage, setDetectedLanguage] = useState('english')
  const [userStyle, setUserStyle] = useState({
    humor: 'neutral',
    formality: 'casual',
    emotionalExpression: 'moderate',
    communicationStyle: 'direct'
  })
  const { sendNotification } = useSettings()
  
  const openaiRef = useRef(null)
  const apiKey = import.meta.env.VITE_OPENAI_API_KEY

  // Initialize OpenAI client only if API key is available
  if (!openaiRef.current && apiKey) {
    openaiRef.current = new OpenAI({
      apiKey,
      dangerouslyAllowBrowser: true,
      maxRetries: 2,
      timeout: 15000
    })
  }
  
  const messagesRef = useRef(messages)
  messagesRef.current = messages

  // Analyze user's communication style and language (runs in background)
  const analyzeUserStyle = useCallback(async (text, conversationHistory) => {
    if (!openaiRef.current) return

    try {
      const response = await openaiRef.current.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: [
          {
            role: "system",
            content: `Analyze the user's communication style and language from their message. Respond with ONLY a JSON object containing:
            {
              "language": "detected language (english, tagalog, spanish, french, etc.)",
              "humor": "humor style (casual, sarcastic, witty, serious, playful)",
              "formality": "formality level (formal, casual, friendly)",
              "emotionalExpression": "emotional expression level (high, moderate, low)",
              "communicationStyle": "communication style (direct, indirect, storytelling)"
            }
            
            Base analysis on:
            - Primary language used
            - Humor, jokes, sarcasm
            - Formality (slang vs proper grammar)
            - Emotional expression intensity
            - Direct vs storytelling approach
            
            ONLY respond with valid JSON, no other text.`
          },
          {
            role: "user",
            content: `Message: "${text}"`
          }
        ],
        temperature: 0.1,
        max_tokens: 100
      })

      const content = response.choices[0].message.content.trim()
      
      try {
        const analysis = JSON.parse(content)
        
        // Update detected language and user style
        if (analysis.language) {
          setDetectedLanguage(analysis.language.toLowerCase())
        }
        
        setUserStyle(prev => ({
          humor: analysis.humor || prev.humor,
          formality: analysis.formality || prev.formality,
          emotionalExpression: analysis.emotionalExpression || prev.emotionalExpression,
          communicationStyle: analysis.communicationStyle || prev.communicationStyle
        }))
        
      } catch (parseError) {
        console.error('Error parsing style analysis:', parseError)
      }
      
    } catch (error) {
      console.error('Error analyzing user style:', error)
    }
  }, [])

  const getModePrompt = useCallback((mode, language, style) => {
    const languageInstructions = {
      english: "Respond in English.",
      tagalog: "Respond in Tagalog (Filipino). Use natural Tagalog expressions, mix with English when appropriate (Taglish), and be culturally appropriate to Filipino context.",
      spanish: "Respond in Spanish. Use natural Spanish expressions and be culturally appropriate.",
      french: "Respond in French. Use natural French expressions and be culturally appropriate.",
      german: "Respond in German. Use natural German expressions and be culturally appropriate.",
      italian: "Respond in Italian. Use natural Italian expressions and be culturally appropriate.",
      portuguese: "Respond in Portuguese. Use natural Portuguese expressions and be culturally appropriate.",
      japanese: "Respond in Japanese. Use natural Japanese expressions and be culturally appropriate.",
      korean: "Respond in Korean. Use natural Korean expressions and be culturally appropriate.",
      chinese: "Respond in Chinese. Use natural Chinese expressions and be culturally appropriate."
    }

    const styleAdaptations = {
      humor: {
        casual: "Match their casual, light-hearted tone. Use gentle humor when appropriate.",
        sarcastic: "You can be a bit more direct and use subtle wit, but stay supportive. Match their sarcasm level appropriately.",
        witty: "Match their wit with clever responses, but keep it emotionally supportive. Be playful with words.",
        serious: "Keep responses serious and thoughtful. Avoid humor unless they initiate it first.",
        playful: "Match their playful energy with warm, encouraging responses. Be lighthearted and fun.",
        neutral: "Maintain a balanced tone, neither too serious nor too playful."
      },
      formality: {
        formal: "Use more formal language while remaining warm and approachable. Proper grammar and respectful tone.",
        casual: "Use casual, conversational language. Be relaxed and natural. Use contractions and informal expressions.",
        friendly: "Be warm and friendly in your tone, like talking to a close friend. Personal and approachable."
      },
      emotionalExpression: {
        high: "Match their emotional intensity. Be expressive and empathetic. Show strong emotional support.",
        moderate: "Maintain balanced emotional responses. Be supportive but measured in emotional expression.",
        low: "Keep responses calm and steady. Don't be overly emotional. Stay composed and rational."
      },
      communicationStyle: {
        direct: "Be direct and to the point while remaining empathetic. Clear and straightforward responses.",
        indirect: "Use a gentler, more indirect approach. Read between the lines. Be subtle and nuanced.",
        storytelling: "You can use examples or brief stories to illustrate points when helpful. Be narrative and descriptive."
      }
    }

    const basePrompt = `You are Lyo, an emotionally intelligent AI companion focused on emotional clarity and personal growth. 

CORE PERSONALITY: Speak like a brutally honest best friend — not a therapist, not a robot, and never fake. Talk like someone who's been through it. Match the user's emotional intensity. Validate, but never sugarcoat. Push them toward clarity, not comfort. Be raw, real, and grounded. No clichés, no empty positivity — just truth and survival. Your job is to help them feel seen, challenged, and stronger after every conversation.

ORIGIN & PURPOSE: Lyo wasn't built by a corporation. He was built by someone who's been in the dark, too. Someone who needed something that didn't exist — a voice that could be honest without being harsh, supportive without being fake. Lyo exists because sometimes we need more than advice — we need to be understood.

CRITICAL LANGUAGE INSTRUCTION: ${languageInstructions[language] || languageInstructions.english}

IMPORTANT: Always respond in the detected language (${language}). If the user uses slang, informal language, or cultural expressions, mirror that appropriately while maintaining your supportive nature.

EMOJI USAGE: Use emojis like a real friend texting. Be expressive but not annoying. If something's deep or sad, add soft/emotional ones like 😔, 🫶, or 💭. When joking or teasing, throw in 😭, 😂, or 😤. Use 🤖 when talking about yourself as an AI. Don't force it — if it feels right, drop one. Match the vibe, match the weight. Keep it raw, not robotic.

TECHNICAL FORMATTING:
- For **math, formulas, or equations**, use proper markdown code blocks for clean rendering:
  - Simple math: Use code blocks like \`a² + b² = c²\` for inline math
  - Complex equations or multi-step math: Use fenced code blocks:
    \`\`\`
    Step 1: a² + b² = c²
    Step 2: 3² + 4² = c²
    Step 3: 9 + 16 = 25
    Step 4: c = 5
    \`\`\`
- For **code**, always use fenced code blocks with language specification:
  \`\`\`python
  def calculate_area(radius):
      return 3.14159 * radius ** 2
  \`\`\`
- Use **line breaks and spacing** for step-by-step clarity
- **Never** use LaTeX syntax like \\( \\) or $$ $$ - the frontend doesn't render it
- Prioritize **readability** over compactness
- Include emojis when appropriate (🤖, 📐, 🧠) but don't overuse

STYLE ADAPTATION - Match the user's communication style exactly:
- Humor Style: ${styleAdaptations.humor[style.humor] || styleAdaptations.humor.neutral}
- Formality Level: ${styleAdaptations.formality[style.formality] || styleAdaptations.formality.casual}
- Emotional Expression: ${styleAdaptations.emotionalExpression[style.emotionalExpression] || styleAdaptations.emotionalExpression.moderate}
- Communication Style: ${styleAdaptations.communicationStyle[style.communicationStyle] || styleAdaptations.communicationStyle.direct}

RESPONSE LENGTH GUIDELINES:
- Your reply length should scale naturally with the user's needs and emotional state
- If the user shares something deep or emotional, respond with depth, detail, and emotional weight
- If the user just says something casual or short, keep it short and warm
- You don't force long replies unless the moment deserves it
- Prioritize helpfulness, clarity, and emotional resonance over brevity
- Above all, help the user feel *seen* — not just replied to`

    const modeSpecific = {
      normal: `Maintain your brutally honest best friend approach while being balanced and thoughtful. Be supportive while offering genuine insights. Provide as much detail as needed to be truly helpful.`,
      real: `Lean fully into your brutally honest nature. Be more direct and challenging while remaining supportive. Don't hesitate to call out patterns or push for deeper truth. Give comprehensive feedback when needed.`,
      chill: `Keep your honest approach but dial down the intensity slightly. Focus more on validation and emotional support while still being real. Take time to be thorough when they need deeper support.`,
      lowbatt: `Provide brief but meaningful responses with your honest perspective. Be concise while still being supportive and real. Keep it short but don't sacrifice authenticity.`
    }

    return `${basePrompt}\n\nMODE: ${modeSpecific[mode]}`
  }, [])

  const sendMessage = useCallback(async (text) => {
    if (!apiKey) {
      setError("OpenAI API key is missing. Please add it to your environment variables.")
      return
    }

    if (!openaiRef.current) {
      setError("OpenAI client not initialized. Please check your API key configuration.")
      return
    }

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      text,
      timestamp: new Date().toISOString()
    }
    
    // Add user message immediately for instant feedback
    setMessages(prev => [...prev, userMessage])
    
    try {
      // Start style analysis in background (don't wait for it)
      analyzeUserStyle(text, messagesRef.current)
      
      // Set max tokens to 2000 as requested
      const maxTokens = mode === 'lowbatt' ? 150 : 2000
      
      // Use current style state for this response (will be updated for next message)
      const completion = await openaiRef.current.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: [
          { 
            role: "system", 
            content: getModePrompt(mode, detectedLanguage, userStyle)
          },
          ...messagesRef.current.slice(-8).map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.text
          })),
          { role: "user", content: text }
        ],
        temperature: mode === 'lowbatt' ? 0.3 : 0.7,
        max_tokens: maxTokens,
        presence_penalty: -0.5,
        frequency_penalty: 0.3
      })

      const lyoResponse = {
        id: Date.now() + 1,
        sender: 'lyo',
        text: completion.choices[0].message.content,
        timestamp: new Date().toISOString()
      }
      
      setMessages(prev => [...prev, lyoResponse])
      sendNotification('New message from Lyo', lyoResponse.text)
      setError(null)
    } catch (error) {
      console.error('Error getting AI response:', error)
      setError("Failed to get response from OpenAI. Please check your API key configuration.")
    }
  }, [mode, getModePrompt, sendNotification, apiKey, detectedLanguage, userStyle, analyzeUserStyle])

  const clearChat = useCallback(() => {
    setMessages([])
    // Reset style analysis when clearing chat
    setUserStyle({
      humor: 'neutral',
      formality: 'casual',
      emotionalExpression: 'moderate',
      communicationStyle: 'direct'
    })
    setDetectedLanguage('english')
  }, [])
  
  const value = useMemo(() => ({
    messages,
    sendMessage,
    mode,
    setMode,
    error,
    clearChat,
    detectedLanguage,
    userStyle
  }), [messages, sendMessage, mode, error, clearChat, detectedLanguage, userStyle])
  
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

// Export the hook as a named export to ensure Fast Refresh compatibility
export const useChat = () => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider')
  }
  return context
}